"use client";

import { useEffect, useRef, useMemo, useState } from "react";
import { getSupabaseBrowserClient } from "@/lib/api/supabase";
import { useUser } from "../hooks/useUser";

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID!;
const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

async function hashNonce(raw: string): Promise<string> {
  const encoded = new TextEncoder().encode(raw);
  const buf = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function loadGsiScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`)) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      const existing = document.querySelector(
        `script[src="${GSI_SCRIPT_SRC}"]`,
      ) as HTMLScriptElement;
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * GIS sets a `g_state` cookie ("Uses third-party cookies" lighthouse
 * audit). Lazy-load it after the user first interacts (mousemove/scroll/
 * keydown) or after a 5 s idle window — gives the page a clean cold
 * start while still enabling One-Tap for engaged visitors.
 */
function useDeferUntilInteraction(enabled: boolean): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!enabled || ready) return;
    const fire = () => setReady(true);
    const opts = { once: true, passive: true } as const;
    window.addEventListener("mousemove", fire, opts);
    window.addEventListener("scroll", fire, opts);
    window.addEventListener("keydown", fire, opts);
    window.addEventListener("touchstart", fire, opts);
    const t = window.setTimeout(fire, 5_000);
    return () => {
      window.removeEventListener("mousemove", fire);
      window.removeEventListener("scroll", fire);
      window.removeEventListener("keydown", fire);
      window.removeEventListener("touchstart", fire);
      window.clearTimeout(t);
    };
  }, [enabled, ready]);
  return ready;
}

export function GoogleOneTap() {
  const { user, loading } = useUser();
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);
  const initializedRef = useRef(false);
  const eligible = !loading && user === null && !!GOOGLE_CLIENT_ID;
  const ready = useDeferUntilInteraction(eligible);

  useEffect(() => {
    if (!eligible || !ready) return;
    if (initializedRef.current) return;

    let cancelled = false;

    async function init() {
      const rawNonce = crypto.randomUUID();
      const hashedNonce = await hashNonce(rawNonce);

      await loadGsiScript();
      if (cancelled) return;

      initializedRef.current = true;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: { credential: string }) => {
          await supabase.auth.signInWithIdToken({
            provider: "google",
            token: response.credential,
            nonce: rawNonce,
          });
          window.location.reload();
        },
        nonce: hashedNonce,
        itp_support: true,
        use_fedcm_for_prompt: true,
      });

      window.google.accounts.id.prompt();
    }

    init().catch(() => {
      /* GIS unavailable (adblocker, etc.) — silently ignore */
    });

    return () => {
      cancelled = true;
      if (initializedRef.current && window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
        initializedRef.current = false;
      }
    };
  }, [eligible, ready, supabase]);

  return null;
}
